/**
 * useFeedback — 反馈（点赞/点踩）与翻译相关逻辑
 * 从 OpsMateContext 中抽取
 */
import { useCallback, useRef, useState } from "react";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { cancelReplyThumbsUp, replyThumbsUp } from "@/services/opsmate/conversation";
import { chenTranslateStream, clearTranslate } from "@/services/opsmate/sessionLog";
import { createFrameThrottle } from "@/utils/frameThrottle";
import { createStreamTypewriter } from "@/utils/streamTypewriter";

interface UseFeedbackParams {
  setItems: React.Dispatch<React.SetStateAction<any[]>>;
}

export function useFeedback({ setItems }: UseFeedbackParams) {
  const { message } = App.useApp();
  const messageRef = useRef(message);
  messageRef.current = message;
  const { t } = useTranslation();

  const [dislikeConfirmVisible, setDislikeConfirmVisible] = useState(false);
  const [dislikeConfirmLoading, setDislikeConfirmLoading] = useState(false);
  const [dislikeConfirmTarget, setDislikeConfirmTarget] = useState<any>(null);

  // ── 关闭点踩确认弹窗 ──
  const closeDislikeConfirm = useCallback(() => {
    setDislikeConfirmVisible(false);
    setDislikeConfirmTarget(null);
    setDislikeConfirmLoading(false);
  }, []);

  // ── 确认点踩 ──
  const confirmDislike = useCallback(async () => {
    const target = dislikeConfirmTarget;
    if (!target?.sessionLogId || !target?.messageKey) {
      closeDislikeConfirm();
      return;
    }
    const { sessionLogId, messageKey } = target;
    setDislikeConfirmLoading(true);
    const hideLoading = messageRef.current.loading({
      content: t("opsmate.feedbackSubmitting"),
      key: "feedback-loading",
      duration: 0,
    });
    try {
      const resp: any = await cancelReplyThumbsUp(sessionLogId);
      const praiseVal = Number(
        resp?.Entity?.UserIsPraise ??
          resp?.Entity?.userIsPraise ??
          resp?.entity?.UserIsPraise ??
          resp?.entity?.userIsPraise ??
          resp?.UserIsPraise ??
          resp?.userIsPraise ??
          0,
      );
      const finalFeedback: "like" | "dislike" | "default" =
        praiseVal === -1 ? "dislike" : praiseVal === 1 ? "like" : "default";

      hideLoading();
      if (finalFeedback === "dislike") {
        messageRef.current.success({
          content: t("opsmate.feedbackMarkedNotUseful"),
          key: "feedback-loading",
        });
      } else {
        messageRef.current.success({
          content: t("opsmate.feedbackUpdated"),
          key: "feedback-loading",
        });
      }

      setItems((prev) =>
        prev.map((item) => {
          if (item.key === messageKey) {
            return {
              ...item,
              extraInfo: {
                ...item.extraInfo,
                feedback: finalFeedback,
              },
            };
          }
          return item;
        }),
      );

      closeDislikeConfirm();
    } catch (e) {
      console.error("点踩确认失败:", e);
      hideLoading();
      messageRef.current.error({
        content: t("opsmate.feedbackRetryLater"),
        key: "feedback-loading",
      });
      setDislikeConfirmLoading(false);
    }
  }, [dislikeConfirmTarget, closeDislikeConfirm, setItems, t]);

  // ── 反馈处理（点赞 / 触发点踩弹窗） ──
  const handleFeedbackChange = useCallback(
    async (itemKey: string, type: "like" | "dislike" | "default", sessionLogId?: string) => {
      if (!sessionLogId) {
        messageRef.current.warning(t("opsmate.missingSessionLog"));
        return;
      }

      // 点踩：先弹二次确认弹窗
      if (type === "dislike") {
        setDislikeConfirmTarget({
          sessionLogId,
          messageKey: itemKey,
        });
        setDislikeConfirmVisible(true);
        return;
      }

      // 点赞
      const hideLoading = messageRef.current.loading({
        content: t("opsmate.feedbackSubmitting"),
        key: "feedback-loading",
        duration: 0,
      });
      let finalFeedback: "like" | "dislike" | "default" = "default";
      try {
        const resp: any = await replyThumbsUp(sessionLogId);
        const praiseVal = Number(
          resp?.Entity?.UserIsPraise ??
            resp?.Entity?.userIsPraise ??
            resp?.entity?.UserIsPraise ??
            resp?.entity?.userIsPraise ??
            resp?.UserIsPraise ??
            resp?.userIsPraise ??
            0,
        );
        hideLoading();
        if (type === "default") {
          messageRef.current.success({
            content: t("opsmate.feedbackUpdated"),
            key: "feedback-loading",
          });
          finalFeedback = "default";
        } else if (praiseVal === 1) {
          messageRef.current.success({
            content: t("opsmate.feedbackMarkedUseful"),
            key: "feedback-loading",
          });
          finalFeedback = "like";
        } else {
          messageRef.current.success({
            content: t("opsmate.feedbackCancelledLike"),
            key: "feedback-loading",
          });
          finalFeedback = "default";
        }

        setItems((prev) =>
          prev.map((i) =>
            i.key === itemKey
              ? { ...i, extraInfo: { ...i.extraInfo, feedback: finalFeedback } }
              : i,
          ),
        );
      } catch (error) {
        console.error("处理反馈失败:", error);
        hideLoading();
        messageRef.current.error({
          content: t("opsmate.feedbackRetryLater"),
          key: "feedback-loading",
        });
      }
    },
    [setItems, t],
  );

  // ── 翻译 ──
  const handleTranslate = useCallback(
    async (itemKey: string, sessionLogId?: string) => {
      const findTarget = (prev: any[]) => prev.find((i) => i.key === itemKey);

      // 需要通过 setItems 回调来读取最新 items（避免闭包过期）
      let shouldToggleOff = false;
      setItems((prev) => {
        const targetItem = findTarget(prev);
        const ei = targetItem?.extraInfo;
        if (
          ei &&
          ("translatedText" in ei ||
            "translatedSummary" in ei ||
            "translatedRationale" in ei)
        ) {
          shouldToggleOff = true;
        }
        return prev; // 不修改，仅检测
      });

      if (shouldToggleOff) {
        setItems((prev) =>
          prev.map((i) => {
            if (i.key === itemKey) {
              const {
                isTranslated: _isTranslated,
                translatedText: _translatedText,
                translatedSummary: _translatedSummary,
                translatedRationale: _translatedRationale,
                ...restExtraInfo
              } = i.extraInfo || {};
              return { ...i, extraInfo: restExtraInfo };
            }
            return i;
          }),
        );
        if (sessionLogId) {
          clearTranslate(sessionLogId).catch((e) => {
            console.error("clearTranslate failed", e);
          });
        }
        return;
      }

      // 先标记为翻译中
      setItems((prev) =>
        prev.map((i) =>
          i.key === itemKey ? { ...i, extraInfo: { ...i.extraInfo, isTranslated: true } } : i,
        ),
      );

      // 各字段视图字符串（typewriter 推进后的"已显示"快照）与 dirty 标志，
      // 由帧节流统一刷给 setItems，避免每 tick 都触发渲染
      type Field = "summary" | "answer" | "rationale";
      const visibleText: Record<Field, string> = { summary: "", answer: "", rationale: "" };
      const dirty: Record<Field, boolean> = {
        summary: false,
        answer: false,
        rationale: false,
      };

      const flushTranslation = createFrameThrottle(() => {
        if (!dirty.summary && !dirty.answer && !dirty.rationale) return;
        const patch: Record<string, string> = {};
        if (dirty.summary) patch.translatedSummary = visibleText.summary;
        if (dirty.answer) patch.translatedText = visibleText.answer;
        if (dirty.rationale) patch.translatedRationale = visibleText.rationale;
        dirty.summary = dirty.answer = dirty.rationale = false;
        setItems((prev) =>
          prev.map((i) =>
            i.key === itemKey ? { ...i, extraInfo: { ...i.extraInfo, ...patch } } : i,
          ),
        );
      });

      // 与聊天答案一致：用 createStreamTypewriter 把 SSE token 平滑成逐字渲染。
      // 每个字段一个独立打字机；onTick 写入对应 visibleText 并触发节流刷新。
      const makeTw = (field: Field) =>
        createStreamTypewriter({
          onTick: (visible) => {
            visibleText[field] = visible;
            dirty[field] = true;
            flushTranslation.schedule();
          },
        });
      const typewriters: Record<Field, ReturnType<typeof createStreamTypewriter>> = {
        summary: makeTw("summary"),
        answer: makeTw("answer"),
        rationale: makeTw("rationale"),
      };

      let currentField: Field | undefined;

      const cleanup = chenTranslateStream(
        sessionLogId || "",
        (chunk) => {
          if (chunk.type === "field") {
            currentField = chunk.field;
          } else if (chunk.type === "token" && currentField && chunk.token) {
            typewriters[currentField].push(chunk.token);
          } else if (chunk.type === "done") {
            // 翻译完成：先让打字机把剩余字符一次吐完，再 flush 节流，最后清除 loading
            typewriters.summary.flush();
            typewriters.answer.flush();
            typewriters.rationale.flush();
            flushTranslation.flush();
            setItems((prev) =>
              prev.map((i) =>
                i.key === itemKey
                  ? { ...i, extraInfo: { ...i.extraInfo, isTranslated: false } }
                  : i,
              ),
            );
          }
        },
        (_err) => {
          console.error("translate stream error");
          typewriters.summary.cancel();
          typewriters.answer.cancel();
          typewriters.rationale.cancel();
          flushTranslation.cancel();
          messageRef.current.error(t("opsmate.translationFailed"));
          setItems((prev) =>
            prev.map((i) => {
              if (i.key === itemKey) {
                const { isTranslated: _isTranslated, ...restExtraInfo } = i.extraInfo || {};
                return { ...i, extraInfo: restExtraInfo };
              }
              return i;
            }),
          );
        },
      );

      // 将 cleanup 存到 ref，以便组件卸载时关闭 SSE（调用方可通过返回值处理）
      void cleanup;
    },
    [setItems, t],
  );

  return {
    dislikeConfirmVisible,
    setDislikeConfirmVisible,
    dislikeConfirmLoading,
    setDislikeConfirmLoading,
    dislikeConfirmTarget,
    setDislikeConfirmTarget,
    closeDislikeConfirm,
    confirmDislike,
    handleFeedbackChange,
    handleTranslate,
  };
}
